import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import SoftBox from "components/SoftBox";
import StatusBadge from "cxfcomponents/StatusBadge";
import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import DataTable from "cxfcomponents/Tables/DataTable";
import CredentialManagementAction from "./components/CredentialManagementAction";
import CredentialDialogForm from "./components/CredentialDialogForm";
import { setSelectedCredential } from "redux/actions/portal/credentialManagementActionCreator";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { formatCredentialApplicationType } from "helpers/credentials";
import CredentialDeleteDialog from "./components/CredentialDeleteDialog";
import CredentialManagementShareUsersModal from "./components/CredentialManagementShareUsersModal";
import CredentialManagementUnshareUsersModal from "./components/CredentialManagementUnshareUsersModal";
import UsersCell from "layouts/Flows/components/UsersCell";
import SoftTypography from "components/SoftTypography";
import defaultCredentialIcon from "assets/images/credential.svg";
import { Card } from "@mui/material";
import Tiles from "./components/tiles";
import { useCredentials } from "../../../hooks/useCredentials";
import { useResourceListener } from "hooks/useResourceListener";

const options = {
   status: [{ value: "active", label: "Active" }]
};

const CredentialManagement = () => {
   const { user } = useAuth0();
   const { user: rUser, users } = useSelector((store) => store?.portal?.users);
   const { credentialManagementFilter, credentialDialogMode } = useSelector((store) => store?.portal?.credentialManagement);
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const [tableView, setTableView] = useState("list");
   const { credentials, handleFetchCredentials } = useCredentials();
   useResourceListener("credentialsSharingUpdate", handleFetchCredentials);

   const isDisableGlobalFilter = (field) => {
      return credentialManagementFilter === field || credentialManagementFilter === "" ? false : true;
   };

   const determineOwnerSharedUsers = (share_user, owner_id) => {
      if (share_user && users) {
         const share_user_ids = share_user?.map((sUser) => sUser?.user_id);
         const sharedUsersAndOwnerData = users?.filter((user) => share_user_ids?.includes(user.auth0_id) || user.auth0_id === owner_id);

         return sharedUsersAndOwnerData;
      }
   };

   const determineShareStatus = (owner_id, share_user) => {
      if (owner_id === user?.cxfUserId) return "Owner";

      return share_user?.some((sharedUser) => sharedUser.user_id === user?.cxfUserId) ? "Shared" : "";
   };

   const dataTableData = useMemo(() => {
      return {
         columns: [
            {
               Header: "icon",
               accessor: "icon",
               canGroupBy: false,
               disableSortBy: true,
               Cell: ({ value }) => <SoftBox width="60px">{value}</SoftBox>
            },
            {
               Header: "Name",
               accessor: "name",
               disableSortBy: false,
               disableGlobalFilter: isDisableGlobalFilter("name"),
               Cell: ({ value }) => (
                  <SoftTypography sx={{ color: "#2D3748" }} className="white-to-dark" fontSize="14px" fontWeight="bold">
                     {value}
                  </SoftTypography>
               )
            },
            {
               Header: "Type",
               accessor: "type",
               disableSortBy: false,
               disableGlobalFilter: isDisableGlobalFilter("applicationType"),
               Cell: ({ value }) => (
                  <SoftTypography fontSize="12px" fontWeight="bold">
                     {value}
                  </SoftTypography>
               )
            },
            {
               Header: "Status",
               accessor: "status",
               disableSortBy: false,
               Cell: ({ value }) => <StatusBadge status={value} />,
               disableGlobalFilter: isDisableGlobalFilter("status")
            },
            {
               Header: "Role",
               accessor: "role",
               disableSortBy: false,
               disableGlobalFilter: isDisableGlobalFilter("owner"),
               Cell: ({ value }) => (
                  <SoftTypography fontSize="12px" fontWeight="bold">
                     {value}
                  </SoftTypography>
               )
            },
            {
               Header: "Expires",
               accessor: "expires",
               disableSortBy: false,
               disableGlobalFilter: isDisableGlobalFilter("expires")
            },
            {
               Header: "Users",
               accessor: "users",
               disableSortBy: true,
               disableGlobalFilter: isDisableGlobalFilter("users"),
               Cell: ({ value }) => <UsersCell users={value} />
            },
            {
               Header: "Created",
               accessor: "created",
               disableSortBy: false,
               disableGlobalFilter: true,
               Cell: ({ value }) => <SoftTypography fontSize="14px">{value}</SoftTypography>
            },
            {
               Header: "Last updated",
               accessor: "last_updated",
               disableSortBy: false,
               defaultSortOrder: "desc",
               disableGlobalFilter: isDisableGlobalFilter("last_updated"),
               sortType: (rowA, rowB) => new Date(rowA.original.last_updated_timestamp || 0) - new Date(rowB.original.last_updated_timestamp || 0),
               Cell: ({ value }) => <SoftTypography fontSize="14px">{value}</SoftTypography>
            },
            {
               Header: "Actions",
               accessor: "actions",
               disableSortBy: true,
               disableGlobalFilter: true
            }
         ].map((column) => ({ ...column, disableResize: true })),
         rows: [
            ...([...credentials]
               .sort((firstCredential, secondCredential) => new Date(secondCredential.updated_date_time || secondCredential.create_date_time || 0) - new Date(firstCredential.updated_date_time || firstCredential.create_date_time || 0))
               .map((credential) => {
               const { application, share_user, owner_id } = credential;
               return {
                  id: credential.id,
                  name: (
                     <SoftTypography variant="text" onClick={() => navigate(`/credential/${credential.id}`)} sx={{ cursor: "pointer" }}>
                        {application?.name}
                     </SoftTypography>
                  ),
                  type: application?.applicationType ? formatCredentialApplicationType(application?.applicationType) : application?.applicationType,
                  status: "active",
                  role: determineShareStatus(owner_id, share_user),
                  users: determineOwnerSharedUsers(share_user, owner_id) || [],
                  created: credential.create_date_time ? formatDateAndTime(getLocalDateTimeString(credential.create_date_time)) : "",
                  last_updated_timestamp: credential.updated_date_time || credential.create_date_time || null,
                  last_updated: credential.updated_date_time || credential.create_date_time ? formatDateAndTime(getLocalDateTimeString(credential.updated_date_time || credential.create_date_time)) : "",
                  actions: <CredentialManagementAction credentialData={credential} iconOrientation="horizontal" />,
                  icon: (
                     <SoftBox
                        component="img"
                        width="100%"
                        height="100%"
                        border={credential?.logo ? "0.5px solid #E6E6E6" : "none"}
                        borderRadius="12px"
                        sx={{
                           objectFit: "contain"
                        }}
                        src={credential?.logo ? `${credential.logo}?v=${credential.updated_date_time ?? ""}` : defaultCredentialIcon}
                     />
                  )
               };
            }) || [])
         ]
      };
   }, [rUser, users, credentialManagementFilter, credentials]);

   useEffect(() => {
      if (credentialDialogMode === "add") {
         dispatch(setSelectedCredential({}));
      }
   }, [credentialDialogMode]);

   return (
      <>
         <div
            style={{
               padding: "8px",
               position: "sticky",
               top: 0,
               zIndex: 1000,
               backgroundColor: "var(--main-bg-color)"
            }}
         >
            <DashboardNavbar />
         </div>
         <DashboardLayout>
            <SoftBox sx={{ mt: -3.2, mb: 0 }}>
               <Card
                  sx={{
                     overflow: "visible",
                     boxShadow: 0,
                     backgroundColor: "transparent !important"
                  }}
               >
                  <DataTable
                     table={dataTableData}
                     entriesPerPage={{ defaultValue: 25 }}
                     hiddenTopButtons={false}
                     options={options}
                     splitted={true}
                     withSearchbar
                     withStatusFilter
                     tableView={tableView}
                     setTableView={setTableView}
                     withTileview
                     withCredentialOptions
                     reloadTableData={handleFetchCredentials}
                     tileViewComponent={(rows) => <Tiles rows={rows} />}
                     tableHeight="clamp(400px, calc(100vh - 84px), calc(100vh - 84px))"
                  />
               </Card>
               <CredentialDialogForm />
               <CredentialDeleteDialog />
               <CredentialManagementShareUsersModal />
               <CredentialManagementUnshareUsersModal />
            </SoftBox>
         </DashboardLayout>
      </>
   );
};

export default CredentialManagement;
